import { useState } from "react"
import type { ProductVisualKind } from "@nova-ai/product-visual-templates/kinds"
import type {
  ProductVisualStudioCard as StudioCard,
  ProductVisualStudioStatus,
  ProductVisualWorklistItem,
  StoreCode,
} from "@nova-ai/shared"
import { AlertCircle, ClipboardList } from "lucide-react"

import { useProductVisualRegeneration } from "../../services/use-product-visual-regeneration"
import { Button } from "../ui/button"
import { Callout } from "../ui/callout"
import { ProductVisualLightbox, type EnlargedProductVisual } from "./product-visual-lightbox"
import { ProductVisualReviewDetails } from "./product-visual-review-details"
import { ProductVisualStudioCard } from "./product-visual-studio-card"

type WorklistStatus = Exclude<ProductVisualStudioStatus, "images">

export function ProductVisualWorklist({
  store,
  visualKind,
  status,
  items,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  error,
  onRetry,
  onLoadMore,
}: {
  store: StoreCode
  visualKind: ProductVisualKind
  status: WorklistStatus
  items: ProductVisualWorklistItem[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error: Error | null
  onRetry: () => Promise<unknown>
  onLoadMore: () => Promise<unknown>
}) {
  const [selected, setSelected] = useState<{
    item: StudioCard
    image: EnlargedProductVisual
  } | null>(null)
  const regeneration = useProductVisualRegeneration({ store })
  if (isLoading) {
    return (
      <div role="status" aria-label="Loading Studio worklist" className="space-y-2">
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className="h-20 animate-pulse rounded-xl bg-muted/60" />
        ))}
      </div>
    )
  }
  if (error && items.length === 0) {
    return (
      <Callout
        tone="destructive"
        icon={<AlertCircle className="size-4" />}
        title="Worklist failed to load"
      >
        <p className="mt-2 text-sm text-destructive">{error.message}</p>

        <Button className="mt-3" onClick={() => void onRetry()}>
          Retry
        </Button>
      </Callout>
    )
  }
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
        <ClipboardList className="size-5" aria-hidden="true" />

        <span>{emptyMessage(status)}</span>
      </div>
    )
  }
  return (
    <div className="space-y-4">
      <ul
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        aria-busy={isFetchingNextPage}
      >
        {items.map((item) => (
          <li key={item.simpleSku}>
            <ProductVisualStudioCard
              item={item}
              store={store}
              visualKind={visualKind}
              regenerationStatus={
                regeneration.isPending && regeneration.variables?.simpleSku === item.simpleSku
                  ? "pending"
                  : "idle"
              }
              regenerationError={
                regeneration.isError &&
                regeneration.variables?.simpleSku === item.simpleSku &&
                regeneration.error
                  ? regeneration.error.message
                  : null
              }
              onRegenerate={() =>
                regeneration.mutate({ simpleSku: item.simpleSku, kind: visualKind })
              }
              onReview={(reviewItem, image) => setSelected({ item: reviewItem, image })}
            />
          </li>
        ))}
      </ul>

      {selected ? (
        <ProductVisualLightbox
          image={selected.image}
          details={
            <ProductVisualReviewDetails
              image={selected.item}
              store={store}
              visualKind={visualKind}
            />
          }
          onClose={() => setSelected(null)}
        />
      ) : null}

      {error ? (
        <Callout tone="destructive" dense title="More worklist items failed to load">
          <p className="mt-1 text-xs text-destructive">{error.message}</p>
        </Callout>
      ) : null}

      {hasNextPage ? (
        <div className="flex justify-center">
          <Button disabled={isFetchingNextPage} onClick={() => void onLoadMore()}>
            {isFetchingNextPage ? "Loading more…" : "Load more"}
          </Button>
        </div>
      ) : (
        <p className="text-center text-xs text-muted-foreground">End of worklist.</p>
      )}
    </div>
  )
}

function emptyMessage(status: WorklistStatus): string {
  if (status === "ready") {
    return "No products are ready to render."
  }
  if (status === "gaps") {
    return "No products have render-input gaps."
  }
  return "No products have a matching renderer exclusion."
}
